import React from "react";
import { motion } from "framer-motion";
import { Users, User, Leaf, ArrowUpRight } from "lucide-react";

const SERVICES_IMAGE = "/session.jpg";

const offerings = [
  {
    icon: User,
    title: "Einzelbegleitung",
    subtitle: "1:1 Begleitetes Malen",
    text: "In geschütztem Rahmen erforschen wir gemeinsam, was in dir Ausdruck sucht, mit Farbe, Form, Material und ehrlichem Gespräch.",
  },
  {
    icon: Users,
    title: "Kleingruppen",
    subtitle: "Gemeinsam kreativ",
    text: "Ein liebevoller Kreis, in dem persönliche Prozesse Platz haben und wir voneinander lernen dürfen.",
  },
  {
    icon: Leaf,
    title: "Ateliertage",
    subtitle: "Auszeit für die Seele",
    text: "Ein ganzer Tag nur für dich und dein kreatives Erleben, begleitet, aber ohne Erwartung.",
  },
];

export default function Services() {
  return (
    <section
      id="angebote"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-cream-alt"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <span className="text-xs uppercase tracking-[0.28em] text-terracotta">
              Angebote · Begleitetes Malen
            </span>
            <h2 className="mt-4 font-serif-elegant text-4xl sm:text-5xl md:text-6xl text-ocean leading-[1.05]">
              Kein Talent nötig,
              <br />
              nur der Mut, <em className="not-italic text-terracotta">hinzuschauen.</em>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 text-lg text-ink/75 font-light leading-relaxed"
          >
            Du musst nichts können. Weder malen noch dich „ausdrücken&ldquo;. Es
            reicht die Bereitschaft, dich selbst neugierig zu begegnen, mit
            allem, was gerade da ist.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {offerings.map((o, i) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`service-card-${o.title.toLowerCase()}`}
              className="group relative p-8 rounded-[1.75rem] bg-cream border border-sage/40 hover:border-terracotta/60 hover:-translate-y-1 transition-all duration-500 hover:shadow-xl hover:shadow-ink/5"
            >
              <div className="w-12 h-12 rounded-xl bg-sage/25 flex items-center justify-center mb-5">
                <o.icon size={22} className="text-ocean" strokeWidth={1.5} />
              </div>
              <div className="text-xs uppercase tracking-[0.22em] text-terracotta">
                {o.subtitle}
              </div>
              <h3 className="font-serif-elegant text-3xl text-ocean mt-2 mb-4">
                {o.title}
              </h3>
              <p className="text-base text-ink/70 leading-relaxed font-light">
                {o.text}
              </p>
              <a
                href="#kontakt"
                className="mt-6 inline-flex items-center gap-1.5 text-sm text-ocean group-hover:text-terracotta transition-colors"
              >
                Kennenlernen
                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-6 relative rounded-[2rem] overflow-hidden aspect-[5/4]"
          >
            <img
              src={SERVICES_IMAGE}
              alt="Kerstin Cloodt beim Malen im Atelier in Kassel · begleitetes Malen"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <h3 className="font-serif-elegant text-3xl md:text-4xl text-ocean leading-tight">
              So könnte eine Sitzung aussehen
            </h3>
            <ol className="mt-8 space-y-5">
              {[
                {
                  n: "01",
                  t: "Ankommen",
                  d: "Ein kurzes Gespräch. Was ist heute da? Was brauchst du?",
                },
                {
                  n: "02",
                  t: "Ausdruck",
                  d: "Farbe, Papier. Dein inneres Bild findet seinen Weg nach außen.",
                },
                {
                  n: "03",
                  t: "Betrachten",
                  d: "Wir schauen gemeinsam hin. Achtsam, wertschätzend, deutend nur, wenn du es möchtest.",
                },
                {
                  n: "04",
                  t: "Mitnehmen",
                  d: "Was zeigt sich? Was möchte weiter wachsen, im Bild und in deinem Leben?",
                },
              ].map((s) => (
                <li key={s.n} className="flex gap-5 items-start">
                  <div className="font-serif-elegant text-2xl text-terracotta w-12 shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-medium text-ocean text-lg">{s.t}</div>
                    <div className="text-ink/70 font-light leading-relaxed">
                      {s.d}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
